import { AdSlotType } from '../../../advertising/AdTypes';
import { DynamicEventType } from '../../../dynamic-events/DynamicEventType';
import { HazardKind, VehicleType, type VehicleHazard } from '../../../hazards/HazardDefinition';
import { WORLD_CONFIG } from '../../../config/world.config';
import { ObstacleType } from '../../../obstacles/ObstacleType';
import { COLLECTIBLE_FLOAT_HEIGHT, collectibleLine, entryConnector, jumpArc, laneArea, laneCenterX, moduleSides, obstacleRow, standardLanes, straightExit } from '../segmentBuilders';
import {
  EnclosureKind,
  SegmentKind,
  type CeilingZone,
  type CollectibleSpawnPoint,
  type FixedObstaclePlacement,
  type ObstacleVariant,
  type SegmentDefinition,
} from '../SegmentDefinition';
import { LEVEL_CONFIG } from '../../../config/level.config';
import { hazardSocket } from '../hazardBuilders';

/**
 * SEG_12 – shell building 60 m (design/01_segments/SEG_12_Shell_Building; the sheet's 50 m
 * standardised to 60 m). The run leads through the open ground floor of a concrete frame:
 * upper floor slabs overhead, columns between the lanes, low site beams, a stair opening in
 * the floor slab and building material on the floor. Open on both sides (daylight).
 *
 *   Slab        z 0–60, underside 4.0 m (soft); visual second floor at 7.6 m
 *   Columns     between the lanes at z 11 / 27 / 55 (0.4 × 0.4 m – no lane change while passing)
 *   Beams       BEAM_A z 15 over all lanes, BEAM_B z 39 over lanes 1–2 – 1.3 m clearance (slide); precast
 *               shoring beams, 0.35 m high, nothing above them but the slab 2.35 m higher: their top
 *               (1.65 m) is walkable – a double jump under the slab clears them (SEGMENT_STANDARD
 *               "Sprungehrlichkeit"), the props stand at the beam ends outside the running lines
 *   Opening     stair hole lane 0, z 23–26.5, 3 m deep (jump) – bottles arc over it
 *   Rows        D1 z 7 and D3 z 50 (11 m after BEAM_B: room for one lane change at 18 m/s) with material
 *               stacks (lane change), D2 z 31 jumps only (pallets, precast blocks)
 *   Lighting    enclosure SHELL (light shade under the slab)
 *   Ads         banner on the slab edge over the entry (AD_SCAFFOLD_HIGH)
 *
 * Floor slab, frame and stairs: greybox (assets/manifest/enclosureGreybox.ts).
 */
export const SHELL = {
  length: 60,
  slab: { clearance: 4.0, thickness: 0.3, secondFloor: 7.6, halfWidth: 7 },
  column: { size: 0.4, z: [11, 27, 55], outerX: 5.2 },
  beam: { clearance: 1.3, depth: 0.35 },
  beamA: { z: 15, fromLane: 0, toLane: 2 },
  beamB: { z: 39, fromLane: 1, toLane: 2 },
  opening: { lane: 0, zFrom: 23, zTo: 26.5, depth: 3 },
  rows: { D1: 7, D2: 31, D3: 50 },
  // on the edge of the second floor: the slab edge is where the runner comes up from the container tower
  portalBanner: { width: 3.6, height: 1.2, y: 8.3, inset: 0.06 },
  /** Rolling cable drums on the upper floor (the slab top): start, meeting line, stop (m). */
  upperReel: { startZ: 46, meetZ: 32, stopZ: 8 },
  collectibleSpacing: 2,
  arc: { spacing: 1.5, overhang: 1 },
} as const;

const ROW_TYPES = [ObstacleType.Jump, ObstacleType.LaneChange] as const;
/** Walking height of the upper floor: the slab top. */
const UPPER_FLOOR = SHELL.slab.clearance + SHELL.slab.thickness;
const SOCKET_UPPER_REEL = 'SOCKET_EVT_UPPER_REEL';

/** A cable drum rolling towards the runner on the upper floor, straight down one lane (columns stand between the lanes). */
const upperReel = (lane: number): VehicleHazard => ({
  id: `HAZ_UPPER_REEL_L${lane}`,
  kind: HazardKind.Vehicle,
  vehicle: VehicleType.CableReel,
  socketId: SOCKET_UPPER_REEL,
  lane,
  startZ: SHELL.upperReel.startZ,
  meetZ: SHELL.upperReel.meetZ,
  stopZ: SHELL.upperReel.stopZ,
  elevation: UPPER_FLOOR,
});
const UPPER_REELS = [upperReel(0), upperReel(1), upperReel(2)];
const half = WORLD_CONFIG.trackWidth / 2;

function columns(): CeilingZone[] {
  const h = SHELL.column.size / 2;
  const boundaries = Array.from({ length: WORLD_CONFIG.laneCount - 1 }, (_, i) => (laneCenterX(WORLD_CONFIG, i) + laneCenterX(WORLD_CONFIG, i + 1)) / 2);
  return SHELL.column.z.flatMap((z, row) =>
    boundaries.map((x, i) => ({
      id: `CEIL_COLUMN_${row + 1}_${i + 1}`,
      zFrom: z - h,
      zTo: z + h,
      xFrom: x - h,
      xTo: x + h,
      clearance: WORLD_CONFIG.groundHeight,
    })),
  );
}

function beam(name: string, spec: { readonly z: number; readonly fromLane: number; readonly toLane: number }): CeilingZone {
  const area = laneArea(WORLD_CONFIG, spec.fromLane, spec.toLane, spec.z - SHELL.beam.depth / 2, spec.z + SHELL.beam.depth / 2);
  const last = WORLD_CONFIG.laneCount - 1;
  // beams rest on the outer columns – they span the curbs too; solid up to their visible top, nothing above
  return {
    id: `CEIL_${name}`,
    ...area,
    xFrom: spec.fromLane === 0 ? -SHELL.slab.halfWidth : area.xFrom,
    xTo: spec.toLane === last ? SHELL.slab.halfWidth : area.xTo,
    clearance: SHELL.beam.clearance,
    top: SHELL.beam.clearance + SHELL.beam.depth,
  };
}

/** Bottles: lanes 1 and 2 straight through, lane 0 in an arc over the stair opening. */
function collectibles(): CollectibleSpawnPoint[] {
  const O = SHELL.opening;
  const center = (O.zFrom + O.zTo) / 2;
  const halfLength = (O.zTo - O.zFrom) / 2 + SHELL.arc.overhang;
  const flat = () => COLLECTIBLE_FLOAT_HEIGHT;
  const along = (lane: number) => collectibleLine(WORLD_CONFIG, `LANE_${lane}`, [{ lane, from: 1, to: SHELL.length - 1, spacing: SHELL.collectibleSpacing, height: flat }]);
  return [
    ...collectibleLine(WORLD_CONFIG, 'OVER_OPENING', [
      { lane: O.lane, from: 1, to: center - halfLength - 1, spacing: SHELL.collectibleSpacing, height: flat },
      { lane: O.lane, from: center - halfLength, to: center + halfLength, spacing: SHELL.arc.spacing, height: jumpArc(COLLECTIBLE_FLOAT_HEIGHT, LEVEL_CONFIG.collectibleJumpArcHeight, center, halfLength) },
      { lane: O.lane, from: center + halfLength + 1, to: SHELL.length - 1, spacing: SHELL.collectibleSpacing, height: flat },
    ]),
    ...along(1),
    ...along(2),
  ];
}

const at = (row: keyof typeof SHELL.rows, lane: number, obstacleId: string): FixedObstaclePlacement => ({ spawnPointId: `SOCKET_OBS_${row}_L${lane}`, obstacleId });
const STACK = 'OBS_MATERIAL_STACK_A';
const PALLET = 'OBS_PALLET_A';
const BLOCK = 'OBS_CONCRETE_BLOCK_A';

const SHELL_LAYOUTS: ObstacleVariant[] = [
  { id: 'STACK_RIGHT_LEFT', weight: 1, hazards: ['HAZ_UPPER_REEL_L0'], placements: [at('D1', 1, PALLET), at('D1', 2, STACK), at('D2', 1, BLOCK), at('D3', 0, STACK), at('D3', 1, PALLET)] },
  { id: 'STACK_LEFT_RIGHT', weight: 1, hazards: ['HAZ_UPPER_REEL_L2'], placements: [at('D1', 0, STACK), at('D1', 2, PALLET), at('D2', 0, PALLET), at('D2', 2, BLOCK), at('D3', 1, BLOCK), at('D3', 2, STACK)] },
  { id: 'STACK_CENTER', weight: 1, hazards: ['HAZ_UPPER_REEL_L1'], placements: [at('D1', 1, STACK), at('D2', 1, BLOCK), at('D2', 2, PALLET), at('D3', 0, PALLET), at('D3', 2, STACK)] },
  { id: 'BLOCKS_OUTER', weight: 1, hazards: ['HAZ_UPPER_REEL_L0'], placements: [at('D1', 0, BLOCK), at('D1', 2, BLOCK), at('D2', 1, PALLET), at('D3', 1, STACK)] },
  { id: 'STACK_RIGHT_BLOCKS', weight: 1, hazards: ['HAZ_UPPER_REEL_L2'], placements: [at('D1', 2, STACK), at('D2', 0, BLOCK), at('D2', 1, BLOCK), at('D3', 0, STACK), at('D3', 2, PALLET)] },
  { id: 'PALLET_ROW', weight: 1, hazards: ['HAZ_UPPER_REEL_L1'], placements: [at('D1', 0, PALLET), at('D1', 1, PALLET), at('D2', 2, BLOCK), at('D3', 1, PALLET), at('D3', 2, STACK)] },
];

export const SEG_SHELL_60M: SegmentDefinition = {
  id: 'SEG_SHELL_60M',
  kind: SegmentKind.Straight,
  displayName: 'Rohbau 60 m',
  length: SHELL.length,
  width: WORLD_CONFIG.trackWidth,
  visualAssetId: 'SEG_SHELL_60M',
  enclosure: { kind: EnclosureKind.Shell, zFrom: 0, zTo: SHELL.length },

  entry: entryConnector(),
  exits: [straightExit(SHELL.length)],
  lanes: standardLanes(WORLD_CONFIG),

  terrain: {
    surfaces: [],
    gaps: [
      {
        id: 'GAP_STAIR_OPENING',
        ...laneArea(WORLD_CONFIG, SHELL.opening.lane, SHELL.opening.lane, SHELL.opening.zFrom, SHELL.opening.zTo),
        xFrom: -half,
        depth: SHELL.opening.depth,
      },
    ],
    ceilings: [
      // the slab top is walkable: the upper floor, reached from the container tower before the shell (user 2026-10-03)
      { id: 'CEIL_SLAB', zFrom: 0, zTo: SHELL.length, xFrom: -SHELL.slab.halfWidth, xTo: SHELL.slab.halfWidth, clearance: SHELL.slab.clearance, top: UPPER_FLOOR, upperFloor: true, soft: true },
      { id: 'CEIL_SECOND_FLOOR', zFrom: 0, zTo: SHELL.length, xFrom: -SHELL.slab.halfWidth, xTo: SHELL.slab.halfWidth, clearance: SHELL.slab.secondFloor, soft: true },
      ...columns(),
      beam('BEAM_A', SHELL.beamA),
      beam('BEAM_B', SHELL.beamB),
    ],
  },

  obstacleSpawnPoints: [
    ...obstacleRow(WORLD_CONFIG, 'D1', SHELL.rows.D1, ROW_TYPES),
    ...obstacleRow(WORLD_CONFIG, 'D2', SHELL.rows.D2, ROW_TYPES),
    ...obstacleRow(WORLD_CONFIG, 'D3', SHELL.rows.D3, ROW_TYPES),
  ],
  obstacleRowLayouts: { sparse: [], dense: [] },
  // Six layouts (user 2026-10-03: "in den Rohbauten … gleich positioniert – Abwechslung"): stacks only in D1 / D3, D2
  // jumps only (4.5 m behind the stair opening).
  obstacleVariants: SHELL_LAYOUTS,
  hazards: UPPER_REELS,

  collectibleSpawnPoints: collectibles(),

  // Open sides: backdrop buildings and cranes beyond the frame (nearer props would stand in the columns).
  decorationSockets: moduleSides(SHELL.length).decorationSockets.filter((s) => s.tags.includes('backdrop')),
  dynamicEventSockets: [hazardSocket(SOCKET_UPPER_REEL, 0, SHELL.upperReel.meetZ, DynamicEventType.ForkliftCrossing)],
  advertisingSlots: [
    {
      id: 'AD_SHELL_SLAB',
      slotType: AdSlotType.ScaffoldHigh,
      // hangs from the slab edge over the entry, facing the approaching runner
      position: { x: 0, y: SHELL.portalBanner.y, z: -SHELL.portalBanner.inset },
      yaw: Math.PI,
      width: SHELL.portalBanner.width,
      height: SHELL.portalBanner.height,
      fillRate: 1,
    },
  ],

  generation: {
    weight: 0.1,
    minDistance: 400,
    cooldownDistance: 300,
    minSegmentsAfterJunction: 1,
    family: 'ENCLOSED',
    tags: ['shell', 'enclosed', 'terrain', 'slide', 'pit'],
  },
};
